import { HostAdapter, HostKind } from "./types";
import { wordAdapter } from "./word";
import { excelAdapter } from "./excel";
import { powerPointAdapter } from "./powerpoint";

// Host detection for the DOCUMENT hosts -- Word, Excel and PowerPoint -- which share
// one bundle and one taskpane.html and are told apart at runtime.
//
// This lives outside platform/index.tsx on purpose. Importing an adapter pulls its
// whole host layer into the bundle (Word's tool surface alone is ~1,750 lines), and
// platform/index.tsx is imported by everything above the seam -- so resolving there
// would put all three document layers into Outlook's bundle, which cannot use any of
// them. The seam's types and React plumbing stay in index.tsx and cost nothing; the
// adapters are reached only from the entry that needs them.

/**
 * Which Office application are we running in?
 *
 * Compared as strings rather than against Office.HostType members because those are
 * only populated once office.js has initialised. Their values ARE these strings.
 *
 * Word is the fallback rather than an error: it is the host this add-in shipped in
 * first, and under Jest there is no Office global at all, so a component test that
 * renders the chat gets a working adapter instead of a crash. Outlook is not reachable
 * from here at all -- it loads its own entry point, so it never asks this question.
 */
export function detectHostKind(): HostKind {
  let host: string | undefined;
  try {
    host = Office?.context?.host as unknown as string | undefined;
  } catch {
    return "word";
  }

  switch (host) {
    case "Excel":
      return "excel";
    case "PowerPoint":
      return "powerpoint";
    default:
      return "word";
  }
}

export function resolveHostAdapter(kind: HostKind = detectHostKind()): HostAdapter {
  switch (kind) {
    case "excel":
      return excelAdapter;
    case "powerpoint":
      return powerPointAdapter;
    case "word":
    default:
      return wordAdapter;
  }
}

let cached: HostAdapter | undefined;

/**
 * The adapter for the running document host, resolved once on first use.
 *
 * Deliberately lazy, not a module-scope constant. Office.context is only reliable
 * after Office.onReady, and this module is imported long before that -- resolving
 * eagerly would read an empty Office.context and silently hand Excel the Word
 * adapter. The entry point calls this inside onReady, so by the time it is first
 * asked the answer is real.
 *
 * The host cannot change during a session -- the runtime belongs to one document in
 * one application -- so caching is safe and there is nothing to invalidate. (That is
 * NOT true in Outlook, where one pane serves many mail items; see platform/outlook.)
 */
export function getDocumentHostAdapter(): HostAdapter {
  if (!cached) {
    cached = resolveHostAdapter();
  }
  return cached;
}
